import type { VisualizationAssetRecord } from "./visualizationAsset";

/**
 * How far a file's conversion into a viewable 3D asset has got, derived
 * entirely from what the server says.
 *
 * Named for what the ingest wizard shows, because that is where the phases are
 * seen, but the question is about a file record rather than about any one
 * screen: the model picker asks it too, to decide whether a `.urdf` can be
 * chosen yet.
 *
 * `failed` is a state of the *conversion* rather than a phase of its own — it is
 * where the run stopped, and the only way on from it is to run again.
 */
export type IngestPhase = "settings" | "converting" | "failed" | "preview";

/**
 * The phase a source file's conversion is in, from its status block.
 *
 * Derived rather than held in local state, so a conversion running in another
 * tab — or one that finished before this dialog was opened — lands the user
 * where the work actually is.
 *
 * ## Staleness
 *
 * A block describing a *different upload* of this file is treated as absent, so
 * re-uploading a URDF does not show the previous upload's robot as this file's
 * preview.
 *
 * The identity compared is `upload_id`, not `version`. `version` is a
 * record-revision counter that any write moves — including the worker's own
 * status write — so comparing it could not distinguish "this artifact came from
 * an older upload" from "this artifact came from this very run", and every
 * completed asset was sent back to Settings the moment the record was refetched.
 * `upload_id` changes if and only if the bytes are genuinely re-uploaded, which
 * is exactly the question being asked. It is also what the artifact's own path
 * is keyed on, so a stale block additionally names a path this file no longer
 * derives.
 *
 * A block that names no upload at all predates this contract: it describes an
 * artifact at a path keyed the old way, which is no longer derivable, so it is
 * stale by construction and treated the same.
 *
 * @param block the parsed `roboto/visualization_asset` metadata, if any
 * @param sourceUploadId the source file's current `upload_id`
 */
export function ingestPhase(
  block: VisualizationAssetRecord | null | undefined,
  sourceUploadId: string | undefined,
): IngestPhase {
  const describesThisUpload =
    block?.source_upload_id !== undefined &&
    block.source_upload_id === sourceUploadId;
  if (!block || !describesThisUpload) {
    return "settings";
  }
  switch (block.status) {
    case "processing":
      return "converting";
    case "failed":
      return "failed";
    case "completed":
      return "preview";
  }
}
